import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

/**
 * Global breadcrumb state. Lets pages deep in the tree push their
 * breadcrumb trail into a single slot rendered by `TopNav`, so the
 * bread-crumbs always appear in the same horizontal strip directly
 * below the main navigation (matching the dark-green nav palette)
 * instead of scattered as tiny grey rows on each page.
 *
 * Usage (inside a page):
 *
 *   const { setBreadcrumbs } = useBreadcrumbs();
 *   useEffect(() => {
 *     setBreadcrumbs([
 *       { label: 'Products', to: '/products' },
 *       { label: 'Stickers' },           // current page
 *     ]);
 *     return () => setBreadcrumbs([]);   // clear on unmount
 *   }, [setBreadcrumbs]);
 *
 * Pages that don't register any breadcrumbs produce no strip — TopNav
 * just renders the main nav row.
 */
const BreadcrumbContext = createContext({
  items: [],
  setBreadcrumbs: () => {},
});

export const BreadcrumbProvider = ({ children }) => {
  const [items, setItems] = useState([]);

  const setBreadcrumbs = useCallback((next) => {
    setItems(Array.isArray(next) ? next : []);
  }, []);

  // Memoised context value — avoids spawning a new object on every render
  // which would invalidate every `useContext(BreadcrumbContext)` consumer.
  const value = React.useMemo(
    () => ({ items, setBreadcrumbs }),
    [items, setBreadcrumbs],
  );

  return (
    <BreadcrumbContext.Provider value={value}>
      {children}
    </BreadcrumbContext.Provider>
  );
};

export const useBreadcrumbs = () => useContext(BreadcrumbContext);

/**
 * Convenience hook — pushes the items onto the global trail on mount
 * and clears them on unmount. Handles the useEffect boilerplate so
 * consumer pages stay tidy.
 */
export const usePageBreadcrumbs = (items) => {
  const { setBreadcrumbs } = useBreadcrumbs();
  // Stringify so pages can pass inline arrays without stale-deps warnings
  const serialized = JSON.stringify(items || []);
  useEffect(() => {
    setBreadcrumbs(items || []);
    return () => setBreadcrumbs([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized, setBreadcrumbs]);
};
